import {
  NotificationCount,
  NotificationList,
  SentMailDetail,
  SentMailList,
  type MailStatus,
} from '@myrewards/contracts';
import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { request } from './client';

// The bell (N4): the person's own notices, newest first; and Admin › Sent mail (System Admins).
// Reading a notice completes nothing: Waiting on you stays the list of work.

const Id = z.uuid();

function path(id: string): string {
  return encodeURIComponent(Id.parse(id));
}

function withQuery(base: string, query: Record<string, string>) {
  const text = new URLSearchParams(query).toString();
  return text === '' ? base : `${base}?${text}`;
}

/** The bell's "Load more" is the one list here that still pages by cursor. */
function withCursor(base: string, cursor: string | null, extra: Record<string, string> = {}) {
  return withQuery(base, cursor === null ? extra : { ...extra, cursor });
}

export const notificationKeys = {
  all: ['notifications'] as const,
  list: ['notifications', 'list'] as const,
  count: ['notifications', 'count'] as const,
};

/** Notices a page at a time, newest first. */
export const notificationsQuery = infiniteQueryOptions({
  queryKey: notificationKeys.list,
  queryFn: ({ pageParam, signal }) =>
    request(withCursor('/api/v1/notifications', pageParam, { limit: '20' }), {
      schema: NotificationList,
      signal,
    }),
  initialPageParam: null as string | null,
  getNextPageParam: (last) => last.nextCursor,
  staleTime: 30_000,
});

/** How many notices are unread: the bell's number. Kept fresh while the app is open. */
export const unreadCountQuery = queryOptions({
  queryKey: notificationKeys.count,
  queryFn: ({ signal }) =>
    request('/api/v1/notifications/unread-count', { schema: NotificationCount, signal }),
  staleTime: 30_000,
  refetchInterval: 60_000,
});

export async function markNoticeRead(id: string): Promise<void> {
  await request(`/api/v1/notifications/${path(id)}/read`, {
    method: 'POST',
    schema: z.undefined(),
  });
}

export function markAllNoticesRead(): Promise<NotificationCount> {
  return request('/api/v1/notifications/read-all', { method: 'POST', schema: NotificationCount });
}

export async function dismissNotice(id: string): Promise<void> {
  await request(`/api/v1/notifications/${path(id)}/dismiss`, {
    method: 'POST',
    schema: z.undefined(),
  });
}

// --- Admin › Sent mail ------------------------------------------------------------------------------

export const sentMailKeys = {
  all: ['sent-mail'] as const,
  list: (status: MailStatus | null, page: number, size: number) =>
    ['sent-mail', 'list', status ?? 'all', page, size] as const,
  one: (id: string) => ['sent-mail', 'one', id] as const,
};

/** Every email the app sent or tried to, newest first, one numbered page at a time; optionally one status. */
export function sentMailQuery(status: MailStatus | null, paging: { page: number; size: number }) {
  return queryOptions({
    queryKey: sentMailKeys.list(status, paging.page, paging.size),
    queryFn: ({ signal }) =>
      request(
        withQuery('/api/v1/admin/sent-mail', {
          ...(status === null ? {} : { status }),
          page: String(paging.page),
          size: String(paging.size),
        }),
        { schema: SentMailList, signal },
      ),
    staleTime: 30_000,
  });
}

/**
 * One email with its text. Every read is written to the audit log (it can hold someone's award
 * details), so it's read once per visit and never refetched behind the person's back.
 */
export function sentMailDetailQuery(id: string) {
  return queryOptions({
    queryKey: sentMailKeys.one(id),
    queryFn: ({ signal }) =>
      request(`/api/v1/admin/sent-mail/${path(id)}`, { schema: SentMailDetail, signal }),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}

/**
 * The server's own content links ("/api/v1/admin/sent-mail/…/html"), accepted only when they
 * are that shape: an iframe or download never points anywhere else.
 */
export function sentMailLink(link: string | null, id: string, kind: 'html' | 'eml'): string | null {
  if (link === null) return null;
  const expected = `/api/v1/admin/sent-mail/${path(id)}/${kind}`;
  return link === expected ? link : null;
}
